/** Thin React wrapper around the pure change-set reducer (`changeSet.ts`).
 *
 * Holds the batch grid's pending edits in a `useReducer` and exposes
 * version-memoized selector hooks. Keyboard handling (undo/redo shortcuts)
 * is a page-level concern and deliberately does NOT live here.
 */
import {
  createContext,
  useContext,
  useMemo,
  useReducer,
  type Dispatch,
  type ReactNode,
} from 'react';

import {
  changeSetReducer,
  editFor,
  initialChangeSetState,
  pendingByCanonicalKey,
  pendingTotals,
  type ChangeSetAction,
  type ChangeSetState,
  type PendingEdit,
} from './changeSet';

interface ChangeSetCtx {
  state: ChangeSetState;
  dispatch: Dispatch<ChangeSetAction>;
}

const Ctx = createContext<ChangeSetCtx | null>(null);

export function ChangeSetProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(changeSetReducer, initialChangeSetState);
  const value = useMemo<ChangeSetCtx>(() => ({ state, dispatch }), [state]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useChangeSet(): ChangeSetCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useChangeSet must be used inside <ChangeSetProvider>');
  return v;
}

// `state.version` increments on exactly every state change — it is the memo
// signal for the selector hooks below (no-op dispatches keep the same state
// object AND version, so the memos stay put).

export function usePendingTotals(): { edits: number; tenants: number } {
  const { state } = useChangeSet();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => pendingTotals(state), [state.version]);
}

export function usePendingByCanonicalKey(): Map<string, number> {
  const { state } = useChangeSet();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => pendingByCanonicalKey(state), [state.version]);
}

export function useTenantEdit(tenantId: number, path: string): PendingEdit | undefined {
  const { state } = useChangeSet();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => editFor(state, tenantId, path), [state.version, tenantId, path]);
}

export function useCanUndoRedo(): { canUndo: boolean; canRedo: boolean } {
  const { state } = useChangeSet();
  return useMemo(
    () => ({ canUndo: state.undoStack.length > 0, canRedo: state.redoStack.length > 0 }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.version],
  );
}
